import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const demoCredentials = {
    email: 'marko@beewise.com',
    password: 'BeeWise2025!'
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    setTimeout(() => {
      if (
        formData?.email === demoCredentials?.email &&
        formData?.password === demoCredentials?.password
      ) {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userEmail', formData?.email);
        if (formData?.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }
        navigate('/main-dashboard');
      } else {
        setErrors({
          general: `Invalid credentials. Demo credentials: ${demoCredentials?.email} / ${demoCredentials?.password}`
        });
        setIsLoading(false);
      }
    }, 1500);
  };

  const handleDemoLogin = () => {
    setFormData({
      email: demoCredentials?.email,
      password: demoCredentials?.password,
      rememberMe: true
    });
  };

  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors?.general && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 flex items-start gap-3 animate-fade-in">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{errors?.general}</p>
          </div>
        )}

        <Input
          type="email"
          name="email"
          label="Email Address"
          placeholder="Enter your email"
          value={formData?.email}
          onChange={handleChange}
          error={errors?.email}
          required
          disabled={isLoading}
        />

        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            label="Password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 p-1 hover:bg-muted/50 rounded transition-colors duration-150"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon 
              name={showPassword ? 'EyeOff' : 'Eye'} 
              size={18} 
              color="var(--color-muted-foreground)" 
            />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <Checkbox
            name="rememberMe"
            label="Remember me"
            checked={formData?.rememberMe}
            onChange={handleChange}
            disabled={isLoading}
          />
          <a
            href="#forgot-password"
            className="text-sm text-primary hover:text-primary/80 transition-colors duration-150 font-medium"
          >
            Forgot password?
          </a>
        </div>

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isLoading}
          iconName="LogIn"
          iconPosition="right"
        >
          Sign In
        </Button>

        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={handleDemoLogin}
          disabled={isLoading}
          iconName="Zap"
          iconPosition="left"
        >
          Use Demo Credentials
        </Button>
      </form>
      <div className="mt-6 text-center">
        <p className="text-sm text-muted-foreground">
          Don't have an account?{' '}
          <a
            href="#register"
            className="text-primary hover:text-primary/80 transition-colors duration-150 font-medium"
          >
            Sign up now
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;